import { useState } from 'react';
import { useParams } from 'react-router-dom';

import styled, { css, keyframes, useTheme } from 'styled-components';
import { ICreatorContentsDetailsDB } from 'type/db';

import BasicColumnChart from 'components/common/chart/BasicColumnChart';
import BasicDonutChart from 'components/common/chart/BasicDonutChart';
import HorizontalBarGraph from 'components/common/chart/HorizontalBarGraph';
import {
	useGetCreatorContentDetails,
	useGetCreatorContents,
} from 'hooks/queries/creator';
import CoreRadioChips from 'shared/chips/CoreRadioChips';
import CoreTooltip from 'shared/CoreTooltip';
import {
	EllipsisText,
	ReportSectionCard,
	ReportSectionText,
	ReportColorCircle,
} from 'styles/styled-components/report';
import style from 'styles/styled-components/style';

import ContentCard from './ContentCard';
import ContentKeyword from './ContentKeyword';

const ad_comment_type = ['광고 관련 질문', '광고 정보 획득', '정보 활용 의지'];
const common_comment_type = ['인플루언서 소통', '콘텐츠 피드백', '기타'];
const comment_type = ['콘텐츠 소재', '일반 댓글'];

const graph_arr = ['종합 성과', '좋아요 수', '댓글 수', '조회 수', '클릭 수(소셜비즈)'];

const ContentReport = ({
	contentIdx,
	contentPk,
}: {
	contentIdx: number;
	contentPk: number;
}) => {
	const { creatorPk } = useParams();
	const { data: creatorContents, isLoading: isLoadingCreatorContents } =
		useGetCreatorContents(creatorPk);

	const selectedContent = creatorContents?.find(
		(content) => content.contentPk === contentPk,
	);

	const { data: contentDetails, isLoading: isLoadingContentDetails } =
		useGetCreatorContentDetails(creatorPk, contentPk);

	const content = contentDetails;
	const theme = useTheme();

	const [selectedChips, setSelectedChips] = useState('종합 성과');
	const [comment_type_chip, setCommentTypeChip] = useState<string>(comment_type[0]);

	const dataMapper: {
		[key: string]: keyof ICreatorContentsDetailsDB['reportGraph'];
	} = {
		'종합 성과': 'totalPerformanceScore',
		'좋아요 수': 'likeCount',
		'댓글 수': 'commentCount',
		'조회 수': 'viewCount',
		'클릭 수(소셜비즈)': 'clickCount',
	} as const;

	const chartData = contentDetails?.reportGraph[dataMapper[selectedChips]];

	return (
		<>
			<Grid flex="start" _gap="16px">
				<ContentCard
					content={selectedContent}
					selected={false}
					_onClick={() => {
						if (selectedContent?.link) window.open(selectedContent.link);
					}}
				/>

				<ReportSectionCard _height="453px">
					<Grid flex="between" _margin="0 0 16px 0">
						<CoreRadioChips
							group_type="carousel"
							style_type="default"
							_width="max-content"
							chips_list={Object.keys(dataMapper)}
							selected_chips={selectedChips}
							setSelectedChips={setSelectedChips}
						/>
					</Grid>
					<Grid flex="start" _height="100%" _gap="16px">
						<BasicColumnChart
							width="742px"
							height="350px"
							data_name={selectedChips}
							dataLabels={false}
							datas={chartData?.map((item) => item.value) ?? []}
							categorys={chartData?.map((item) => item.date) ?? []}
							zoom={false}
							trend={1}
							xTickAmount={3}
						/>
					</Grid>
				</ReportSectionCard>
			</Grid>

			<Grid>
				<ReportSectionCard>
					<ReportSectionText typo="span" _margin="0 0 8px 0">
						URL별 클릭 수
					</ReportSectionText>
					<Grid flex="between" direction="column" flex_option="2 1 0" _gap="8px">
						{content?.clickCountsByUrl ? (
							content?.clickCountsByUrl?.slice(0, 3)?.map((data, idx) => (
								<Grid key={idx} flex="start" _gap="24px">
									<Grid _width="160px" flex="start" _gap="16px">
										<Text
											_width="9px"
											typo="p_bold"
											color={theme.colors.gray.gray80_text}
										>
											{idx + 1}
										</Text>
										<Grid flex="between" _gap="8px">
											<Grid flex="start" _gap="4px">
												<EllipsisText typo="span">클릭 수</EllipsisText>
											</Grid>
											<EllipsisText _width="100%" typo="span">
												{data.value}회
											</EllipsisText>
										</Grid>
									</Grid>
									<KeywordBox flex="start" _height="100%">
										<Text
											color={theme.colors.gray.gray80_text}
											white_space="normal"
											typo="p_bold"
										>
											<a
												href={content?.clickCountsByUrl?.[idx]?.url}
												target="_blank"
												rel="noopener noreferrer"
												style={{
													textDecoration: 'underline',
												}}
											>
												&nbsp;{content?.clickCountsByUrl?.[idx]?.url}
											</a>
										</Text>
									</KeywordBox>
								</Grid>
							))
						) : (
							<>
								<KeywordBox>
									<Text
										color={theme.colors.gray.gray80_text}
										white_space="normal"
										typo="p_bold"
									>
										&nbsp; 분석가능한 URL이 없습니다.
									</Text>
								</KeywordBox>

								<KeywordBox>
									<Text
										color={theme.colors.gray.gray80_text}
										white_space="normal"
										typo="p_bold"
									>
										&nbsp; 분석가능한 URL이 없습니다.
									</Text>
								</KeywordBox>
								<KeywordBox>
									<Text
										color={theme.colors.gray.gray80_text}
										white_space="normal"
										typo="p_bold"
									>
										&nbsp; 분석가능한 URL이 없습니다.
									</Text>
								</KeywordBox>
							</>
						)}
					</Grid>
				</ReportSectionCard>
			</Grid>
			<Grid flex="start" _gap="16px">
				<ReportSectionCard _width="max-content" _height="284px">
					<Grid _width="200px" flex="between">
						<ReportSectionText typo="span" _margin="0 0 8px 0">
							전체 댓글 비율
						</ReportSectionText>
					</Grid>
					<Grid flex="start" _gap="4px">
						<ReportColorCircle color_type="primary" />
						<ReportSectionText typo="p_bold">광고 관련 댓글</ReportSectionText>
					</Grid>
					<Grid flex="start" _gap="4px">
						<ReportColorCircle color_type="primary_darken" />
						<ReportSectionText typo="p_bold">일반 댓글</ReportSectionText>
					</Grid>

					<Grid _width="100%">
						<BasicDonutChart
							value={
								content?.overallCommentRate?.commentCategories?.map(
									(category) => Math.round(category.percentage) ?? 0,
								) ?? [0, 0]
							}
							max_label={true}
							unit="%"
							legend_show={false}
							value_name={['광고 관련 댓글', '일반 댓글']}
						/>
						<Grid flex="center">
							<Text typo="p">{'*소셜비즈 키워드 제외'}</Text>
						</Grid>
					</Grid>
				</ReportSectionCard>
				<ReportSectionCard _height="284px">
					<Grid flex="between">
						<ReportSectionText typo="span" _margin="0 0 8px 0">
							댓글 주요 키워드
						</ReportSectionText>
					</Grid>
					<Grid _width="100%" _height="200px">
						{content?.topCommentKeywords ? (
							<ContentKeyword
								keyword={
									content?.topCommentKeywords?.map((item, idx) => ({
										text: item.keyword,
										value: item.score,
									})) ?? []
								}
							/>
						) : (
							<Text typo="span_bold">분석 가능한 댓글이 없습니다.</Text>
						)}
					</Grid>
				</ReportSectionCard>
			</Grid>
			<Grid>
				<ReportSectionCard>
					<Grid flex="between">
						<ReportSectionText typo="span" _margin="0 0 8px 0">
							댓글 주요 문장
						</ReportSectionText>
					</Grid>
					<Grid flex="start" _gap="16px" align_items="stretch">
						<KeywordBox flex_option="1 1 0">
							<ReportSectionText typo="span" _margin="0 0 8px 0">
								전체 댓글 비율
							</ReportSectionText>
							<CoreRadioChips
								group_type="carousel"
								style_type="default"
								chips_list={comment_type}
								_width="100%"
								selected_chips={comment_type_chip}
								setSelectedChips={setCommentTypeChip}
							/>
							{comment_type_chip === comment_type[0] ? (
								<Grid _margin="16px 0 0 0">
									<HorizontalBarGraph
										data={{
											'광고 관련 질문':
												content?.overallCommentRate?.commentCategories[0]?.categories
													?.adQuestions ?? 0,
											'광고 정보 획득':
												content?.overallCommentRate?.commentCategories[0]?.categories
													?.adInformation ?? 0,
											'정보 활용 의지':
												content?.overallCommentRate?.commentCategories[0]?.categories
													?.adIntent ?? 0,
										}}
										max={100}
										background={true}
									/>
								</Grid>
							) : (
								<Grid _margin="16px 0 0 0">
									<HorizontalBarGraph
										data={{
											'인플루언서 소통':
												content?.overallCommentRate?.commentCategories[1]?.categories
													?.influencerInteraction ?? 0,
											'콘텐츠 피드백':
												content?.overallCommentRate?.commentCategories[1]?.categories
													?.contentFeedback ?? 0,
											기타:
												content?.overallCommentRate?.commentCategories[1]?.categories
													?.others ?? 0,
										}}
										max={100}
										background={true}
									/>
								</Grid>
							)}
						</KeywordBox>
						<Grid flex="between" direction="column" flex_option="2 1 0" _gap="8px">
							{content?.topCommentSentences ? (
								content?.topCommentSentences?.slice(0, 3)?.map((data, idx) => (
									<KeywordBox key={idx} flex="start" _height="100%">
										<Text
											color={theme.colors.gray.gray80_text}
											white_space="normal"
											typo="p_bold"
										>
											✏️&nbsp;{data}
										</Text>
									</KeywordBox>
								))
							) : (
								<>
									<KeywordBox>
										<Text
											color={theme.colors.gray.gray80_text}
											white_space="normal"
											typo="p_bold"
										>
											✏️&nbsp; 분석가능한 댓글이 없습니다.
										</Text>
									</KeywordBox>

									<KeywordBox>
										<Text
											color={theme.colors.gray.gray80_text}
											white_space="normal"
											typo="p_bold"
										>
											✏️&nbsp; 분석가능한 댓글이 없습니다.
										</Text>
									</KeywordBox>
									<KeywordBox>
										<Text
											color={theme.colors.gray.gray80_text}
											white_space="normal"
											typo="p_bold"
										>
											✏️&nbsp; 분석가능한 댓글이 없습니다.
										</Text>
									</KeywordBox>
								</>
							)}
						</Grid>
					</Grid>
				</ReportSectionCard>
			</Grid>
		</>
	);
};

const { Text, Grid } = style;

const post_ani = keyframes`
    0% {
      	opacity: 1;
    }
    50% {
		opacity: 0.5;
    }
    100% {
		opacity: 1;
    }
`;

const PostBox = styled.div<{ animation?: boolean; delay?: string }>`
	width: 200px;
	height: 200px;
	flex-shrink: 0;
	border-radius: 8px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	overflow: hidden;
	position: relative;
	${({ animation }) =>
		animation &&
		css`
			animation: ${post_ani} 2s infinite ease-in-out;
		`}
	${({ delay }) => delay && `animation-delay: ${delay}`};
`;

const PostImg = styled.div<{ picture?: string; color?: string }>`
	position: relative;
	padding-top: 100%;
	overflow: hidden;
	background-color: '#f8f8f8';
	background: url(${({ picture }) => picture}) no-repeat center / cover,
		url('/image/small_no_image.png') no-repeat center / cover;
`;
const KeywordBox = styled(Grid)`
	padding: 8px 16px;
	border-radius: 8px;
	border: 1px solid ${({ theme }) => theme.colors.gray.gray10};
	background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
`;

const RelativeGrid = styled(Grid)`
	position: relative;
`;
const AbsoluteChart = styled.div`
	position: absolute;
	top: 20px;
`;

export default ContentReport;
